<template>
  <!-- Not assigned: violet outline CTA -->
  <button
    v-if="featureEnabled && !isAssigned && !readonly"
    type="button"
    class="ajft-cta"
    :disabled="disabled"
    data-testid="assigned-job-family-cta"
    @click="onAssign"
  >
    <JobFamilyIcon class="ajft-icon" />
    <PlusIcon class="ajft-plus" />
    <span class="ajft-cta-label">Job Family</span>
  </button>

  <!-- Assigned: removable gray tag -->
  <span v-else-if="featureEnabled && isAssigned" class="ajft-tag" data-testid="assigned-job-family-tag">
    <JobFamilyIcon class="ajft-icon" />

    <span class="ajft-name">{{ jobFamily.name }}</span>

    <span v-if="level" class="ajft-level" data-testid="assigned-job-family-level">
      {{ levelLabel }}
    </span>

    <EXTooltip
      v-if="!readonly"
      class="ajft-close-tip"
      title="Remove job family"
      placement="top"
      :delay="100"
      :disabled="disabled"
      show-on-child-focus
    >
      <button
        type="button"
        class="ajft-close"
        :disabled="disabled"
        aria-label="Remove job family"
        data-testid="assigned-job-family-close"
        @click="onUnassign"
      >
        <TagCloseIcon />
      </button>
    </EXTooltip>
  </span>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";

import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import JobFamilyIcon from "./icons/JobFamilyIcon.vue";
import PlusIcon from "./icons/PlusIcon.vue";
import TagCloseIcon from "./icons/TagCloseIcon.vue";

/**
 * ER-11649 — AssignedJobFamilyTag (position form Job Family control).
 *
 * Tag/chip control for assigning a Job Family + Level to a position, with two states:
 * - Unassigned (`jobFamily=null`): a violet outline "+ Job Family" CTA that emits
 *   `assign` (the parent opens JobFamilyPickerDialog).
 * - Assigned (`jobFamily` set): a gray removable tag showing the job family name,
 *   a level badge (ordinal · name format, e.g. "3 · Senior Engineer"), and a close
 *   (×) button that emits `unassign`.
 *
 * Controlled presentational component: the parent owns `jobFamily` / `level` objects.
 * No API calls inside the component. Labels are resolved live from the `level` prop
 * on each render — never cached.
 *
 * Shipped behind the linked idea flag (idea-MDP-581, "Job Family — Discipline &
 * Career-Ladder Grouping for Positions"). Renders by default; set the flag to
 * false to hide the control.
 */
const props = defineProps({
  /** Job Family object: { id, name } | null. Null = unassigned state. */
  jobFamily: {
    type: Object,
    default: null,
  },
  /** Level object: { id, ordinal, name } | null. Shown as "ordinal · name". */
  level: {
    type: Object,
    default: null,
  },
  /** Disabled state — disables both CTA and close button. */
  disabled: {
    type: Boolean,
    default: false,
  },
  /** Read-only mode (Rule 8): hides the "+ Job Family" CTA and the × button. */
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["assign", "unassign"]);

const app = getCurrentInstance();
const { proxy } = app || {};
const featureEnabled = ref(true);

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
});

const isAssigned = computed(() => props.jobFamily !== null && props.jobFamily !== undefined);

const levelLabel = computed(() => {
  if (!props.level) return "";
  const { ordinal, name } = props.level;
  return `${ordinal} · ${name}`;
});

function onAssign() {
  emit("assign");
}

function onUnassign() {
  emit("unassign");
}
</script>

<style scoped>
.ajft-icon,
.ajft-plus {
  flex-shrink: 0;
}

/* Not-filled CTA button (lilac outline) */
.ajft-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 28px;
  padding: 6px 10px;
  background: #f9f5ff;
  border: 1px solid #d6bbfb;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  color: #53389e;
  cursor: pointer;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
}

.ajft-cta:hover {
  background: #f4ebff;
  border-color: #d6bbfb;
}

.ajft-cta-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}

/* Filled tag (pill) */
.ajft-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 2px 8px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 999px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
  color: #344054;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
}

.ajft-name {
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

/* Level badge (outlined pill, ordinal · name format) */
.ajft-level {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border: 1.5px solid #475467;
  border-radius: 999px;
  color: #344054;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
}

.ajft-close-tip {
  display: inline-flex;
  line-height: 0;
}

.ajft-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: transparent;
  border-radius: 3px;
  cursor: pointer;
}

.ajft-close:hover {
  background: #f2f4f7;
}
</style>
